/**
 * ZoomableMap - Interactive SVG map with pan/zoom support.
 * Cooperative mode: two-finger to pan/zoom on mobile; single-finger scrolls page.
 * Fullscreen mode (CSS fixed): single-finger pan allowed.
 * Dual-circle markers: outer = hold color, inner = difficulty color + points.
 * Search highlight pulse, new route pulse, climbed badge.
 * Spray wall markers (Spraywall add-on): label-less rounded squares on top of
 * the route markers; a tap opens the wall on the Spraywall tab.
 * The pan / pinch / rotate / wheel-zoom math lives in utils/panZoomMath.js
 * (D50); this component keeps the gesture state and the DOM listeners.
 */
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "../i18n";
import { newRouteCutoff } from "../constants";
import { getRouteDisplayName } from "../utils/routeDisplayUtils";
import { haptic } from "../utils/haptics";
import { localStore } from "../utils/safeStorage";
import { statusBarInset } from "../utils/iosStatusBar";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { useAppTheme } from "../contexts/ThemeContext";
import {
  rotatePoint, unrotatePoint, fingerGeometry, isPinchActivated, pinchFrameFactor,
  isRotationActivated, clampViewBox, zoomViewBoxAt, wheelZoomFactor, pinchPanViewBox,
  rotationStep, panOrigin, exceedsSlop, TAP_SLOP_PX, POINTER_DRAG_SLOP_PX, sprayMarkerFarScale,
} from "../utils/panZoomMath";
import { SprayWallMarkerGlyph } from "./ui";

// Read/write the rotation override for a given map. Stored as a float
// (degrees, normalized to [0, 360)) per-(gymId, mapId) in localStorage so
// a user who twisted the map to match how they walked into the gym keeps
// it across sessions, independently per map (downstairs vs upstairs may
// be set up differently).
function loadRotation(persistKey) {
  if (!persistKey) return 0;
  const raw = localStore.get(`sendly-map-rot-${persistKey}`);
  if (raw == null) return 0;
  const v = parseFloat(raw);
  if (!Number.isFinite(v)) return 0;
  return ((v % 360) + 360) % 360;
}
function saveRotation(persistKey, rot) {
  if (!persistKey) return;
  localStore.set(`sendly-map-rot-${persistKey}`, String(rot));
}

// SVG transform string applied to a <g> wrapping the BACKGROUND content.
// Rotates around the inner center; the viewBox size never changes with
// rotation so the surrounding layout stays put — corners may extend
// outside the visible area at non-quarter angles, exactly the same way
// content gets clipped when you zoom in.
function rotationTransform(rot, mw, mh) {
  if (rot === 0) return "";
  return `rotate(${rot} ${mw / 2} ${mh / 2})`;
}

// rotatePoint / unrotatePoint (INNER route-doc coords ↔ display coords) live
// in utils/panZoomMath.js since D50; re-exported here so existing importers
// (zoomableMapRotation.test.js) keep their import path.
export { rotatePoint, unrotatePoint };

export function ZoomableMap({ mapWidth, mapHeight, mapImageUrl, mapBgColor, difficulty: DIFFICULTY, holdColors: HOLD_COLORS,
  routes, doneSet, compToppedRouteIds, onRouteClick, onMapClick, isPlacing, searchQuery, onRouteLongPress, repositionId, newRouteDays,
  quickLogActive = false, quickLogCounts = null, onToggleQuickLog = null, onOpenFilters = null, activeFilterCount = 0,
  archiveActive = false, onToggleArchive = null,
  persistKey, focusRequest, compRouteIds, today,
  sprayWalls = [], onSprayWallClick = null, placingSprayWallId = null }) {
  const { accent, th: theme } = useAppTheme();

  const { t } = useTranslation();
  // Rotation in degrees, free-form (any angle, normalized [0, 360)).
  // Driven by the two-finger pinch+twist gesture. Persisted per map so
  // a gym member who twisted the map to match how they entered the room
  // gets the same orientation back next visit. CW positive.
  const [rotation, setRotation] = useState(() => loadRotation(persistKey));
  useEffect(() => { setRotation(loadRotation(persistKey)); }, [persistKey]);
  // The viewBox dimensions stay at (mapWidth × mapHeight) regardless of
  // rotation — the layout box around the SVG never resizes during a
  // twist, so the route list below stays put. Rotated content's corners
  // simply extend outside the viewBox at non-quarter angles (the same
  // way the corners disappear off the edges when the user zooms in).
  // Pan/zoom handle the same way as before because the coordinate space
  // doesn't change shape when rotation changes.
  const contentTransform = rotationTransform(rotation, mapWidth, mapHeight);
  const longPressRef = useRef(null);
  const longPressFired = useRef(false);
  const longPressOrigin = useRef(null);
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  // Two-finger rotation gesture. Free-form — the map can land at any
  // angle, no 90° snap. Pinch zoom and pan run in parallel in the same
  // gesture (already implemented above); twisting adds rotation on top
  // BUT only after a clear-intent threshold:
  //
  // The natural pinch motion (fingers moving toward / away from each
  // other) produces small incidental angle changes — typically 1-4° per
  // frame from finger geometry alone, with no rotational intent. Without
  // a threshold every pinch zoom would also rotate the map slightly. To
  // disambiguate, we accumulate the signed angle delta during the
  // gesture and only START applying rotation once the accumulated value
  // crosses ROTATION_ACTIVATION_DEG. From that frame onward each
  // subsequent delta applies live; the pre-activation slack is discarded.
  // Crucially, hitting the threshold does NOT lock pinch zoom out — both
  // continue to apply on every frame for the rest of the gesture.
  // Each axis (zoom, rotation) has an independent activation threshold so
  // a clear-intent pinch never sneaks in a tiny rotation, and a clear-
  // intent twist never sneaks in a tiny zoom. Pan is always active —
  // midpoint movement is the unambiguous "pan" signal so it never needs
  // gating. Each threshold is measured from the ABSOLUTE start of the
  // gesture (not last frame), so slow drift over a long swipe doesn't
  // trickle past the threshold the way per-frame accumulation would.
  // The thresholds themselves (ROTATION_ACTIVATION_DEG 12°, the 1° per-frame
  // rotation dead zone, PINCH_ACTIVATION_RATIO ±5%) and the math that applies
  // them live in utils/panZoomMath.js (D50); the refs below are the gesture
  // STATE and stay here.
  const lastAngleRef = useRef(null);              // radians, last frame's finger-line angle
  const gestureStartAngleRef = useRef(null);      // radians, finger-line angle at gesture start
  const gestureRotationActiveRef = useRef(false); // once true, rotation applies live this gesture
  const gestureStartDistRef = useRef(null);       // pixels, finger spread at gesture start
  const pinchActiveRef = useRef(false);           // once true, factor follows actual finger-spread
  // Mirror rotation into a ref so the touchend listener (registered once
  // in a useEffect) reads the latest value when persisting to localStorage,
  // not the stale closure value from when the listeners were attached.
  const currentRotationRef = useRef(rotation);
  useEffect(() => { currentRotationRef.current = rotation; }, [rotation]);
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: mapWidth, h: mapHeight });
  const viewBoxRef = useRef(viewBox);
  viewBoxRef.current = viewBox;
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);
  const lastDistRef = useRef(null);
  const lastMidRef = useRef(null);
  // Track the two fingers by their Touch.identifier. iOS Safari can reorder
  // e.touches between events (especially on small screens like iPhone 13 mini
  // where the fingers sit close together), so indexing by [0] and [1] causes
  // the distance to jump when the indexes swap — visible as map "vibration".
  const pointer0IdRef = useRef(null);
  const pointer1IdRef = useRef(null);
  // Live touch count kept in sync with the native touch listeners. The
  // long-press timer callback reads this at fire time so that a two-finger
  // gesture which started *after* the timer was armed still cancels the
  // long-press — the old "cancel on touchstart" fix only catches the case
  // where the second finger arrives before 500ms.
  const activeTouchCountRef = useRef(0);
  const isPlacingRef = useRef(isPlacing);
  isPlacingRef.current = isPlacing;

  // Cooperative touch overlay
  const [showCoopHint, setShowCoopHint] = useState(false);
  const coopTimerRef = useRef(null);
  // Tap pulse — ring that briefly expands on the tapped marker so the user
  // sees their tap registered before the modal opens. Cleared after 300ms.
  const [tappedId, setTappedId] = useState(null);
  const tappedTimerRef = useRef(null);
  // Deferred-pulse timer used by the external focus useEffect (route list
  // → map jump). Kept alongside tappedTimerRef so unmount cleans up both.
  const focusPulseTimerRef = useRef(null);
  const flashTap = useCallback((routeId) => {
    if (tappedTimerRef.current) clearTimeout(tappedTimerRef.current);
    setTappedId(routeId);
    tappedTimerRef.current = setTimeout(() => setTappedId(null), 400);
  }, []);
  useEffect(() => () => { if (tappedTimerRef.current) clearTimeout(tappedTimerRef.current); }, []);
  // Suppress coop hint briefly after a successful two-finger gesture
  const lastGestureEndRef = useRef(0);
  // Track whether fullscreen single-finger touch actually panned (vs tap)
  const didPanRef = useRef(false);
  const touchStartTargetRef = useRef(null);

  // CSS-based fullscreen (works on iOS too, unlike Fullscreen API)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isFullscreenRef = useRef(false);
  isFullscreenRef.current = isFullscreen;

  // Clamp viewBox so its center stays within map bounds (20% overscroll
  // margin) — see clampViewBox in utils/panZoomMath.js.
  const clamp = useCallback((vb) => clampViewBox(vb, mapWidth, mapHeight), [mapWidth, mapHeight]);

  const zoom = useCallback((factor, cx, cy) => {
    setViewBox(v => zoomViewBoxAt(v, factor, cx, cy, mapWidth, mapHeight));
  }, [mapWidth, mapHeight, clamp]);

  const getSvgPoint = useCallback((clientX, clientY) => {
    const svg = containerRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  }, []);

  // Get SVG-units-per-screen-pixel scale from the CTM (handles preserveAspectRatio correctly)
  const getSvgScale = useCallback(() => {
    const svg = containerRef.current;
    if (!svg) return { sx: 1, sy: 1 };
    const ctmInv = svg.getScreenCTM()?.inverse();
    if (!ctmInv) {
      const rect = svg.getBoundingClientRect();
      const v = viewBoxRef.current;
      return { sx: v.w / rect.width, sy: v.h / rect.height };
    }
    return { sx: ctmInv.a, sy: ctmInv.d };
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const pt = getSvgPoint(e.clientX, e.clientY);
    const factor = wheelZoomFactor(e.deltaY);
    zoom(factor, pt.x, pt.y);
  }, [getSvgPoint, zoom]);

  // Wheel zoom MUST be wired as a NATIVE non-passive listener — React's
  // onWheel is registered passively (same as touch), so e.preventDefault()
  // in a React onWheel handler is silently ignored and the page scrolls
  // BEHIND the map zoom. Attaching natively with { passive: false } lets
  // preventDefault actually swallow the wheel event, so scrolling over the
  // map only zooms; scrolling beside the map still scrolls the page (events
  // outside the SVG never reach this listener).
  useEffect(() => {
    const svg = containerRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handlePointerDown = useCallback((e) => {
    if (e.pointerType === "touch") return;
    // Placing mode: we still want drag-to-pan, but only commit a placement
    // tap if the pointer didn't actually move. didPanRef gets flipped on move.
    setIsPanning(true);
    didPanRef.current = false;
    const v = viewBoxRef.current;
    const { sx, sy } = getSvgScale();
    panStartRef.current = { x: e.clientX, y: e.clientY, vx: v.x, vy: v.y, sx, sy };
  }, [getSvgScale]);

  const handlePointerMove = useCallback((e) => {
    // Desktop drag cancels long-press
    clearTimeout(longPressRef.current); longPressRef.current = null;
    if (!panStartRef.current) return;
    const ps = panStartRef.current;
    if (exceedsSlop(e.clientX - ps.x, e.clientY - ps.y, POINTER_DRAG_SLOP_PX)) {
      didPanRef.current = true;
    }
    const origin = panOrigin(ps, e.clientX, e.clientY);
    setViewBox(v => clamp({ ...v, ...origin }));
  }, [clamp]);

  const handlePointerUp = useCallback(() => {
    setIsPanning(false); panStartRef.current = null;
  }, []);

  // Show cooperative hint with auto-hide
  const flashCoopHint = useCallback(() => {
    setShowCoopHint(true);
    if (coopTimerRef.current) clearTimeout(coopTimerRef.current);
    coopTimerRef.current = setTimeout(() => setShowCoopHint(false), 1500);
  }, []);

  // Native touch listeners with passive: false — React synthetic touch events are
  // passive by default, so e.preventDefault() is silently ignored and the browser
  // applies its own pinch-zoom on top of ours, causing jitter.
  // During gestures we write viewBox directly to the DOM (svg.setAttribute) to
  // bypass React re-renders of the entire marker tree, then sync to React state
  // on touchend.
  useEffect(() => {
    const svg = containerRef.current;
    if (!svg) return;

    // SVG scale from CTM (accounts for preserveAspectRatio letterboxing)
    const ctmScale = () => {
      const ctmInv = svg.getScreenCTM()?.inverse();
      if (ctmInv) return { sx: ctmInv.a, sy: ctmInv.d };
      const rect = svg.getBoundingClientRect();
      const v = viewBoxRef.current;
      return { sx: v.w / rect.width, sy: v.h / rect.height };
    };

    // Apply viewBox directly to DOM (skip React render cycle for smooth animation)
    const applyViewBox = (vb) => {
      svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
      viewBoxRef.current = vb;
    };

    // Look up a specific Touch by identifier in a TouchList (safer than
     // indexing, because iOS can reorder touches between events).
    const findTouchById = (touchList, id) => {
      if (id == null) return null;
      for (let i = 0; i < touchList.length; i++) {
        if (touchList[i].identifier === id) return touchList[i];
      }
      return null;
    };

    // Lock onto a specific pair of fingers and record their distance/midpoint
    // as the gesture baseline. Called both at gesture start and when a finger
    // is swapped mid-gesture so we re-base instead of jumping.
    const beginTwoFingerGesture = (t0, t1) => {
      pointer0IdRef.current = t0.identifier;
      pointer1IdRef.current = t1.identifier;
      const { dist, angle: ang, midX, midY } = fingerGeometry(t0, t1);
      lastDistRef.current = dist;
      lastMidRef.current = { x: midX, y: midY };
      // Re-anchor the rotation reference angle when we re-base — without
      // this, swapping fingers mid-gesture would inject a spurious delta.
      lastAngleRef.current = ang;
      // Re-anchor the activation thresholds too: if a finger swaps mid
      // gesture, treat the post-swap state as a fresh baseline so the
      // user has to twist or pinch from THIS state to (re)activate the
      // corresponding mode. Pre-existing active modes stay active —
      // they're stored in pinchActiveRef / gestureRotationActiveRef and
      // are only cleared by resetGestureState (lift all fingers).
      gestureStartDistRef.current = dist;
      gestureStartAngleRef.current = ang;
      panStartRef.current = null;
    };

    const onTouchStart = (e) => {
      activeTouchCountRef.current = e.touches.length;
      if (e.touches.length >= 2) {
        // Two (or more) fingers: we take over — prevent browser scroll/zoom
        // Cancel any pending long-press
        clearTimeout(longPressRef.current); longPressRef.current = null;
        e.preventDefault();
        setShowCoopHint(false);
        // Fresh gesture: reset the per-axis activation flags. Each mode
        // (pinch zoom, rotation) starts inactive and only kicks in once
        // the user clearly intends it (separate thresholds, see refs at
        // the top of this component).
        gestureRotationActiveRef.current = false;
        pinchActiveRef.current = false;
        beginTwoFingerGesture(e.touches[0], e.touches[1]);
      } else if (e.touches.length === 1) {
        if (isPlacingRef.current) {
          // Placing: enable single-finger pan, but defer preventDefault to
          // touchmove (only after we detect real movement) so a stationary
          // tap still lets the OS synthesize a click → handleSvgClick fires
          // with the correct clientX/Y for placement. didPanRef gates the
          // click in handleSvgClick to suppress placement after a drag.
          didPanRef.current = false;
          touchStartTargetRef.current = e.target;
          const v = viewBoxRef.current;
          const { sx, sy } = ctmScale();
          panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, vx: v.x, vy: v.y, sx, sy, vw: v.w, vh: v.h };
        } else if (isFullscreenRef.current) {
          // Fullscreen: single-finger pan — prevent scroll
          e.preventDefault();
          didPanRef.current = false;
          touchStartTargetRef.current = e.target;
          const v = viewBoxRef.current;
          const { sx, sy } = ctmScale();
          panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, vx: v.x, vy: v.y, sx, sy, vw: v.w, vh: v.h };
        } else {
          // Cooperative: do NOT preventDefault — browser scrolls the page
          panStartRef.current = null;
        }
      }
    };

    const onTouchMove = (e) => {
      activeTouchCountRef.current = e.touches.length;
      // Any touch movement cancels long-press
      clearTimeout(longPressRef.current); longPressRef.current = null;

      // Find our two tracked fingers (by identifier, not by index)
      const t0 = findTouchById(e.touches, pointer0IdRef.current);
      const t1 = findTouchById(e.touches, pointer1IdRef.current);

      if (t0 && t1 && lastDistRef.current && lastMidRef.current) {
        e.preventDefault();
        const { dist, angle, midX: newMidX, midY: newMidY } = fingerGeometry(t0, t1);

        // ── Pinch zoom activation (independent threshold) ──
        // Until the user has spread their fingers more than ±5% from
        // the gesture-start distance, treat this as a pure pan / rotate
        // gesture and ignore the per-frame zoom factor. Once they've
        // crossed the threshold, pinch zoom kicks in and stays on for
        // the rest of the gesture. Tracks ABSOLUTE start, not last
        // frame — slow drift doesn't accumulate past the gate.
        if (!pinchActiveRef.current && isPinchActivated(dist, gestureStartDistRef.current)) {
          pinchActiveRef.current = true;
        }
        // Per-frame sensor-noise dead zone (sub-1% changes) is applied
        // inside pinchFrameFactor, only while pinch is active.
        const factor = pinchFrameFactor(dist, lastDistRef.current, pinchActiveRef.current);

        // ── Rotation activation (independent threshold) ──
        // Compare the CURRENT angle against the gesture-start baseline
        // (signed, wrap-around safe). Once the absolute deviation
        // crosses ROTATION_ACTIVATION_DEG, rotation kicks in. Per-frame
        // dead zone applies during ACTIVE rotation only, to suppress
        // jitter while the user is intentionally rotating.
        if (!gestureRotationActiveRef.current && isRotationActivated(angle, gestureStartAngleRef.current)) {
          gestureRotationActiveRef.current = true;
        }

        const rect = svg.getBoundingClientRect();

        // Pan + pinch: the map point under the previous finger midpoint
        // lands under the new midpoint at the new zoom (factor=1 → pure pan).
        applyViewBox(clamp(pinchPanViewBox({
          v: viewBoxRef.current, rect, prevMid: lastMidRef.current,
          newMid: { x: newMidX, y: newMidY }, factor, mapWidth, mapHeight,
        })));

        // ── Rotation application (after pinch math has positioned vb) ──
        // Pivot is the FINGER MIDPOINT (see rotationStep). It must read the
        // JUST-APPLIED viewBox, because pinch zoom may have changed v.w/v.h
        // this frame.
        if (gestureRotationActiveRef.current) {
          const step = rotationStep({
            v: viewBoxRef.current, rect, mid: { x: newMidX, y: newMidY },
            angle, lastAngle: lastAngleRef.current, currentRot: currentRotationRef.current,
            mapWidth, mapHeight,
          });
          if (step) {
            setRotation(step.rotation);
            applyViewBox(clamp(step.viewBox));
          }
        }

        // Keep the React `viewBox` STATE in lockstep with the imperatively-
        // applied DOM viewBox for the WHOLE two-finger gesture (zoom, pan,
        // AND rotate). The SVG renders `viewBox={vb}` from state, so any
        // mid-gesture re-render reasserts that controlled prop. Pinch frames
        // used to write only the DOM (applyViewBox) and skip state; when the
        // rotation block fired a setRotation/setViewBox on a neighboring
        // frame, its re-render reverted the controlled viewBox to the STALE
        // pre-zoom state — the "pinch zooms a bit then snaps back" bug. The
        // lag here is ≤1 frame and self-correcting (next frame re-applies),
        // so it's imperceptible — same as the already-smooth rotate path.
        setViewBox(viewBoxRef.current);

        lastAngleRef.current = angle;
        lastDistRef.current = dist;
        lastMidRef.current = { x: newMidX, y: newMidY };
      } else if (e.touches.length === 1) {
        if (panStartRef.current && (isFullscreenRef.current || isPlacingRef.current)) {
          // Single-finger pan using saved scale (fullscreen always; placing
          // mode opts in once the finger crosses the tap-tolerance threshold).
          const ps = panStartRef.current;
          const moved = exceedsSlop(e.touches[0].clientX - ps.x, e.touches[0].clientY - ps.y, TAP_SLOP_PX);
          if (moved) didPanRef.current = true;
          // In placing mode, hold off on preventDefault until we know the
          // user is actually dragging — otherwise a clean tap loses its
          // synthesized click and placement breaks.
          if (isFullscreenRef.current || didPanRef.current) e.preventDefault();
          if (didPanRef.current) {
            applyViewBox(clamp({ ...panOrigin(ps, e.touches[0].clientX, e.touches[0].clientY), w: ps.vw, h: ps.vh }));
          }
        } else if (!isFullscreenRef.current && !isPlacingRef.current) {
          // Cooperative: flash hint, but suppress briefly after a two-finger gesture
          if (Date.now() - lastGestureEndRef.current > 600) {
            flashCoopHint();
          }
        }
      }
    };

    const resetGestureState = () => {
      lastDistRef.current = null;
      lastMidRef.current = null;
      pointer0IdRef.current = null;
      pointer1IdRef.current = null;
      panStartRef.current = null;
      didPanRef.current = false;
      touchStartTargetRef.current = null;
      lastAngleRef.current = null;
      gestureStartAngleRef.current = null;
      gestureStartDistRef.current = null;
      gestureRotationActiveRef.current = false;
      pinchActiveRef.current = false;
    };

    // Persist the current rotation when a two-finger gesture finishes.
    // Rotation already updated live during the gesture; this is just the
    // localStorage commit. Reads via currentRotationRef so the freshest
    // post-batch value is saved even when setRotation fired moments ago.
    const persistRotationFromState = () => {
      saveRotation(persistKey, currentRotationRef.current);
    };

    const onTouchEnd = (e) => {
      activeTouchCountRef.current = e.touches.length;
      // Record gesture end time for coop hint cooldown
      if (lastDistRef.current !== null || lastMidRef.current !== null) {
        lastGestureEndRef.current = Date.now();
      }
      // Fullscreen: if single-finger touch ended without panning, synthesize click
      // — fullscreen touchstart calls preventDefault, which suppresses the OS's
      // own click synthesis. Skip in placing mode: there we never call
      // preventDefault on a stationary tap, so the OS produces a click
      // naturally with proper clientX/Y; a manual dispatch here would double-fire.
      if (isFullscreenRef.current && !isPlacingRef.current && !didPanRef.current && touchStartTargetRef.current && e.touches.length === 0) {
        const el = touchStartTargetRef.current;
        // Walk up to find clickable SVG <g> with route click handler
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }

      // If at least one tracked finger is still down AND another finger remains,
      // re-base to the current pair rather than ending the gesture. This makes
      // two-finger gestures resilient to brief finger lifts (common on small
      // screens where fingers are close and one can momentarily stop touching).
      const t0Still = findTouchById(e.touches, pointer0IdRef.current);
      const t1Still = findTouchById(e.touches, pointer1IdRef.current);
      if ((!t0Still || !t1Still) && e.touches.length >= 2) {
        beginTwoFingerGesture(e.touches[0], e.touches[1]);
      } else if (!t0Still && !t1Still) {
        // Both tracked fingers are gone: gesture truly ended — persist the
        // current (live-updated) rotation to localStorage, then wipe state.
        persistRotationFromState();
        resetGestureState();
      } else if (e.touches.length < 2) {
        // Only one of our fingers remains and no replacement — end gesture.
        // This is the most common path (user lifted one finger after a
        // pinch+rotate). Persist rotation, then wipe everything via the
        // shared reset (which also clears the rotation-activation flags
        // so the next pinch starts at a fresh threshold).
        persistRotationFromState();
        resetGestureState();
      }

      // Sync final viewBox to React state for consistent marker rendering
      setViewBox(viewBoxRef.current);
    };

    const onTouchCancel = (e) => {
      activeTouchCountRef.current = e?.touches?.length || 0;
      // iOS sends touchcancel when the system interrupts the gesture (e.g.,
      // notification, phone call). Wipe all gesture state so the next
      // touchstart re-initializes cleanly.
      resetGestureState();
      setViewBox(viewBoxRef.current);
    };

    svg.addEventListener('touchstart', onTouchStart, { passive: false });
    svg.addEventListener('touchmove', onTouchMove, { passive: false });
    svg.addEventListener('touchend', onTouchEnd);
    svg.addEventListener('touchcancel', onTouchCancel);
    return () => {
      svg.removeEventListener('touchstart', onTouchStart);
      svg.removeEventListener('touchmove', onTouchMove);
      svg.removeEventListener('touchend', onTouchEnd);
      svg.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [clamp, mapWidth, mapHeight, flashCoopHint, persistKey]);

  // CSS fullscreen toggle (works on iOS unlike Fullscreen API)
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(fs => {
      const next = !fs;
      document.body.style.overflow = next ? "hidden" : "";
      return next;
    });
  }, []);

  // Esc key to exit fullscreen — through the shared stack, so a modal opened
  // over the fullscreen map takes the key first and the map stays as it is.
  useEscapeKey(toggleFullscreen, isFullscreen);

  // Cleanup body overflow on unmount
  useEffect(() => () => { document.body.style.overflow = ""; }, []);

  const handleSvgClick = useCallback((e) => {
    if (!isPlacing) return;
    // Suppress the placement click if the pointer/touch actually dragged the
    // map — the user wanted to pan, not drop a route.
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    const pt = getSvgPoint(e.clientX, e.clientY);
    // pt is in OUTER (post-rotation) coords; the route's stored x/y are in
    // INNER coords (the original map orientation). Invert the rotation so
    // dropping a marker while the map is rotated still saves it where the
    // user expected — and reads back at the same screen location.
    const inner = unrotatePoint(pt.x, pt.y, rotation, mapWidth, mapHeight);
    onMapClick(Math.round(inner.x), Math.round(inner.y));
  }, [isPlacing, getSvgPoint, onMapClick, rotation, mapWidth, mapHeight]);

  // Reset clears rotation back to 0° too (and persists), and snaps the
  // viewBox back to a full-extent default. Without the rotation reset
  // the user has no way to undo a twist gesture short of twisting back,
  // which is awkward — the ⟲ button is the natural "back to default".
  const resetView = () => {
    if (rotation !== 0) {
      setRotation(0);
      saveRotation(persistKey, 0);
    }
    setViewBox({ x: 0, y: 0, w: mapWidth, h: mapHeight });
  };

  // External focus request — typically fired when the user taps a route on
  // the routes tab list ("show me where this is on the map"). Zooms in to
  // the route's location and runs the regular tap-pulse animation on the
  // marker so the route is easy to spot in the new viewport. Each request
  // carries a fresh timestamp so repeat clicks on the same route still
  // trigger (object identity changes even when the id matches).
  useEffect(() => {
    if (!focusRequest?.id) return;
    const r = routes.find(x => x.id === focusRequest.id);
    if (!r || r.x == null || r.y == null) return;
    // Forward-rotate the route's INNER coord to find its display position.
    const outer = rotatePoint(r.x, r.y, rotation, mapWidth, mapHeight);
    // Modest zoom level — closer than the default "see everything" view
    // so the highlighted marker is unmissable, but not so close that the
    // user loses spatial context for nearby routes on the wall.
    const newW = mapWidth / 2.5;
    const newH = newW * (mapHeight / mapWidth);
    const nextVb = clamp({
      x: outer.x - newW / 2,
      y: outer.y - newH / 2,
      w: newW,
      h: newH,
    });
    setViewBox(nextVb);
    viewBoxRef.current = nextVb;
    // Pull the map into view BEFORE firing the pulse. INSTANT scroll —
    // smooth scroll takes 300-500ms which outlasts the 380ms tap-pulse
    // keyframe, so the user lands looking at the map after the pulse
    // has already finished.
    //
    // `block: "center"` aligns the wrapper's vertical center with the
    // viewport center, which is also where the viewBox places the
    // highlighted route's marker — so the user reliably lands looking
    // at the focused marker in the middle of the screen.
    //
    // The earlier `"nearest"` value was broken on mobile when crossing
    // tabs: switching from Routes tab (long scroll) to Map via
    // `handleShowOnMap` preserved the document scrollY. The shorter
    // MapTab couldn't reach that scrollY, so `"nearest"` interpreted
    // the wrapper as "above the viewport" and pulled its BOTTOM edge
    // to the viewport bottom — putting the focused marker well below
    // the visible area.
    if (wrapperRef.current?.scrollIntoView) {
      wrapperRef.current.scrollIntoView({ block: "center", behavior: "instant" });
    }
    // Slight delay before the pulse: without it the animation starts
    // the same frame the viewBox jumps to the new position, and the
    // user's eye doesn't catch the highlight because they're still
    // registering "the map just changed". The 220 ms beat lets the
    // brain settle on the new view, then the 380 ms pulse keyframe
    // unambiguously says "this is the route". Cancel any previous
    // pending pulse so rapid clicks don't stack.
    if (focusPulseTimerRef.current) clearTimeout(focusPulseTimerRef.current);
    focusPulseTimerRef.current = setTimeout(() => {
      focusPulseTimerRef.current = null;
      flashTap(r.id);
    }, 220);
  }, [focusRequest, routes, rotation, mapWidth, mapHeight, clamp, flashTap]);

  // Cleanup the pending focus-pulse timer on unmount so we don't fire
  // setState on a torn-down component.
  useEffect(() => () => {
    if (focusPulseTimerRef.current) clearTimeout(focusPulseTimerRef.current);
  }, []);

  const vb = viewBox.x + " " + viewBox.y + " " + viewBox.w + " " + viewBox.h;

  // Check if a route matches search
  const isSearchMatch = (r) => {
    if (!searchQuery) return false;
    const q = searchQuery.toLowerCase();
    const displayName = getRouteDisplayName(r, DIFFICULTY, HOLD_COLORS, t).toLowerCase();
    return displayName.includes(q) || (r.name || "").toLowerCase().includes(q) || (r.setterName || "").toLowerCase().includes(q);
  };

  // Check if route is "new" using the admin-configured threshold
  // (`settings.newRouteDays`, shared with the TV dashboard — see MapTab).
  // Day-granularity date-string compare via the shared `newRouteCutoff` helper
  // so the app map and every TV slide agree on which routes are new. A route's
  // `createdAt` is a gym-timezone date string with no time component, so the
  // old millisecond math only added UTC skew + a mid-day off-by-one flip.
  const newRouteCutoffStr = newRouteCutoff(newRouteDays, today);
  const isNewRoute = (r) => !!r.createdAt && r.createdAt >= newRouteCutoffStr;

  // Prefer the auto-detected per-map edge color over the global theme
  // map-bg: when the gym's floor plan has a clean white/beige border, we
  // store that color on the map record and bleed it into the viewport so
  // the wall image visually extends to the wrapper edge instead of
  // sitting on the default beige theme background.
  const thBg = mapBgColor || theme?.mapBg || "#f0f1f3";
  const thBorder = theme?.border || "#e0e0e0";

  // Fullscreen is a `position:fixed; inset:0` layer, so it covers the strip the
  // sticky header normally reserves — on iPad the count badge would sit behind
  // the clock. 0 everywhere but an iPad-style PWA.
  const fsTop = isFullscreen ? statusBarInset() : 0;

  // The bottom-right control stack lands in the physical corner of the screen
  // in fullscreen, and a phone's rounded display corner clipped the ✖ button's
  // edge. There is no way to measure that radius: the only web API for it is
  // `env(safe-area-inset-*)`, which stays 0 without `viewport-fit=cover` — and
  // cover is deliberately off (see the comment in index.html: it
  // redefines `vh` and every modal sizes itself in vh). So the inset is a
  // constant sized to clear the radii phones actually ship (~25–55px): 32px up
  // from the bottom while 12px in from the right keeps the stack outside the
  // arc, and it doubles as clearance for the gesture bar. The env() term is a
  // no-op today and turns itself on if viewport-fit ever changes.
  //
  // Armed quick log needs more: its bar is `min(400px, 100vw - 32px)` wide and
  // fixed 20px off the bottom, so below ~432px of viewport it runs under this
  // stack — and it paints above the fullscreen layer. Clearing it means the
  // bar's own offset + its height + a gap, which is also why the stack must not
  // simply be raised to that height always: away from quick log it would float
  // pointlessly far from the corner it belongs to.
  const ctrlBottom = !isFullscreen
    ? 12
    : quickLogActive
      ? "calc(env(safe-area-inset-bottom, 0px) + 20px + 48px + 12px)"
      : "calc(env(safe-area-inset-bottom, 0px) + 32px)";

  // Marker scale, shared by the route markers and the spray wall markers:
  // simplified dots when zoomed out, fixed-size detail when zoomed in.
  const zoomLevel = mapWidth / viewBox.w;
  const showDetail = zoomLevel > 1.6;
  // Screen-aware scale: ~18px visual radius on all screen sizes
  const cw = containerRef.current?.getBoundingClientRect()?.width || 400;
  const dotScale = showDetail ? (18 * viewBox.w) / (14 * cw) : 1;

  return (
    <div ref={wrapperRef} style={{
      position: isFullscreen ? "fixed" : "relative",
      inset: isFullscreen ? 0 : undefined,
      zIndex: isFullscreen ? 50 : undefined,
      margin: isFullscreen ? 0 : "0 0 12px",
      borderRadius: isFullscreen ? 0 : 16,
      border: isFullscreen ? "none" : "1px solid " + thBorder,
      background: thBg, overflow: "hidden",
      aspectRatio: isFullscreen ? undefined : `${mapWidth} / ${mapHeight}`,
      // Non-fullscreen: `pan-x pan-y` lets one-finger page scroll through the
      // embedded map but blocks the browser's own pinch / double-tap zoom, so
      // a two-finger gesture can never leak into a page-level zoom (which would
      // detach `position:fixed` chrome — e.g. the new-route FAB — from the
      // viewport bottom). The map's own pinch still works: its native
      // touchstart calls preventDefault, which takes precedence over CSS.
      touchAction: isFullscreen ? "none" : "pan-x pan-y",
    }}>
      {/* Route count badge */}
      <div style={{
        position: "absolute", top: 10 + fsTop, left: 10, zIndex: 5,
        background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)",
        borderRadius: 10, padding: "4px 10px", fontSize: 12, fontWeight: 600,
        color: "#333",
      }}>
        {t('map.routeCount', { count: routes.length })}
      </div>

      {/* Cooperative touch hint overlay */}
      {showCoopHint && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          background: "rgba(0,0,0,0.45)", display: "flex",
          alignItems: "center", justifyContent: "center",
          pointerEvents: "none", transition: "opacity 0.3s",
        }}>
          <div style={{
            background: "rgba(0,0,0,0.75)", color: "#fff",
            padding: "12px 24px", borderRadius: 12,
            fontSize: 14, fontWeight: 600, textAlign: "center",
            maxWidth: 260,
          }}>
            {t('zoomMap.useTwo')}
          </div>
        </div>
      )}

      <style>{`
        @keyframes sendly-tap-pulse {
          from { transform: scale(0.3); opacity: 1; }
          to   { transform: scale(1);   opacity: 0; }
        }
        .sendly-tap-ring {
          animation: sendly-tap-pulse 380ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
          transform-box: fill-box;
          transform-origin: center;
          pointer-events: none;
          vector-effect: non-scaling-stroke;
        }
      `}</style>
      <svg
        ref={containerRef}
        data-testid="route-map"
        viewBox={vb}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: "100%",
          height: isFullscreen ? "100%" : undefined,
          display: "block",
          cursor: isPlacing ? "crosshair" : isPanning ? "grabbing" : "grab",
          // See the container's touchAction note: `pan-x pan-y` outside
          // fullscreen blocks browser pinch/double-tap zoom on the actual
          // touch target (this SVG) while leaving one-finger page scroll and
          // the map's own JS-driven pinch intact.
          touchAction: isFullscreen ? "none" : "pan-x pan-y",
          // Safari on iPad shows a translucent gray box on SVG tap targets
          // unless we explicitly disable the default tap highlight.
          WebkitTapHighlightColor: "transparent",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        /* wheel + touch handled natively via useEffect for passive: false */
        onClick={handleSvgClick}
      >
        <defs>
          <pattern id="hatch-unset" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke={accent} strokeWidth="1.5" strokeOpacity="0.5" />
          </pattern>
        </defs>

        {/* Rotation wrapper — applied INSIDE the SVG so the SVG's
            getScreenCTM() stays unrotated and the existing pan/zoom math
            (which only uses ctmInv.a / .d, expecting no skew) keeps
            working unchanged. The viewBox swaps w/h for 90°/270° to fit
            the rotated content. Coordinates flowing OUT (markers' inner
            x/y rendered here) need no adjustment; coordinates flowing IN
            (taps) get inverse-rotated in handleSvgClick before being
            saved as route x/y. */}
        <g transform={contentTransform}>

        {/* Background image or placeholder */}
        {mapImageUrl ? (
          <image href={mapImageUrl} x="0" y="0" width={mapWidth} height={mapHeight} />
        ) : (
          <>
            <rect x="0" y="0" width={mapWidth} height={mapHeight} fill={thBg} />
            {/* Wall panel grid */}
            {Array.from({ length: Math.ceil(mapHeight / 80) }, (_, row) =>
              Array.from({ length: Math.ceil(mapWidth / 80) }, (_, col) => {
                const shade = 225 + ((row * 7 + col * 13) % 20);
                return <rect key={`p${row}_${col}`} x={col * 80} y={row * 80} width="80" height="80"
                  fill={`rgb(${shade},${shade - 3},${shade - 8})`} stroke="#ddd" strokeWidth="0.5" />;
              })
            )}
            {/* S-curve from Sendly logo */}
            {(() => {
              const pad = 40;
              const sx = (mapWidth - 2 * pad) / 46;
              const sy = (mapHeight - 2 * pad) / 88;
              const ox = pad - 42 * sx;
              const oy = pad - 18 * sy;
              const f = (ix, iy) => `${(ix * sx + ox).toFixed(1)},${(iy * sy + oy).toFixed(1)}`;
              const d = `M${f(88,18)} C${f(88,18)} ${f(42,18)} ${f(42,44)} C${f(42,62)} ${f(88,62)} ${f(88,80)} C${f(88,106)} ${f(42,106)} ${f(42,106)}`;
              return <>
                <path d={d} fill="none" stroke={accent} strokeWidth="36" strokeLinecap="round" opacity="0.08" />
                <path d={d} fill="none" stroke={accent} strokeWidth="3" strokeLinecap="round" opacity="0.15" strokeDasharray="10,8" />
              </>;
            })()}
            <text x={mapWidth / 2} y={mapHeight / 2} textAnchor="middle" dominantBaseline="central"
              fill={accent} fontSize="13" fontWeight="700" fontFamily="'Space Grotesk',sans-serif"
              letterSpacing="3" opacity="0.35">{t('zoomMap.uploadHint')}</text>
          </>
        )}

        </g>{/* /rotation wrapper — markers below stay upright */}

        {/* Route markers — simplified dots when zoomed out, fixed-size detail when zoomed in */}
        {(() => {
          return routes.map(r => {
            if (repositionId && r.id === repositionId) return null;
            const hasD = r.difficulty != null;
            const hasH = r.holdColor != null;
            const d = hasD ? (DIFFICULTY[r.difficulty] || { color: "#ccc", border: "#999", label: "?", points: 0, tc: "#333" }) : null;
            const h = hasH ? (HOLD_COLORS[r.holdColor] || { color: "#ddd", label: "?" }) : null;
            // The climbed tick answers "have I sent this", and during a
            // competition that has a second source: a top on the viewer's own
            // scorecard. Those never become `ascents` docs — they pay no gym
            // points — so without this set every competition boulder read as
            // untouched however many the competitor had already sent.
            const ok = doneSet.has(r.id) || !!compToppedRouteIds?.has(r.id);
            // Marker position in OUTER (viewBox) coords. The route doc's
            // x/y are in the original (un-rotated) map space; we forward-
            // rotate them here so the marker lands at the visually correct
            // spot, while the marker glyph itself stays upright.
            const rx = rotation === 0 ? r.x : rotatePoint(r.x, r.y, rotation, mapWidth, mapHeight).x;
            const ry = rotation === 0 ? r.y : rotatePoint(r.x, r.y, rotation, mapWidth, mapHeight).y;

            // Determine marker colors based on which fields are set
            // Outer: holdColor → solid, no holdColor → hatched accent
            const markerFill = hasH ? h.color : "url(#hatch-unset)";
            const pulseColor = hasD ? d.color : accent;
            const isCompRoute = !!(compRouteIds && compRouteIds.has(r.id));
            // A competition route carries NO point number. The gym's difficulty
            // points say what the route pays into a climber's lifetime gym
            // score; no scoring system inside the competition pays them, and
            // the competition's own value (fixed redpoint points, or a live
            // pool share) would not fit in a 14px circle anyway. The inner
            // difficulty circle stays — the grade still reads off its colour.
            const pointsText = hasD && !isCompRoute ? d.points + "p" : null;
            const innerColor = hasD ? d.color : null;
            const innerBorder = hasD ? d.border : null;
            const innerTc = hasD ? d.tc : null;

            if (!showDetail) {
              const isNew = isNewRoute(r);
              // Small: holdColor → solid fill; no holdColor → hatched accent
              const smallFill = hasH ? h.color : "url(#hatch-unset)";
              const isTapped = tappedId === r.id;
              return (
                <g key={r.id} data-testid="map-marker" data-route-id={r.id}
                  onClick={(e) => { if (isPlacing || longPressFired.current) return; e.stopPropagation(); flashTap(r.id); setTimeout(() => onRouteClick(r), 180); }}
                  onPointerDown={(e) => {
                    if (isPlacing || !onRouteLongPress) return;
                    // Don't arm a new timer if a multi-touch gesture is
                    // already in progress. iOS Safari with touch-action:none
                    // (fullscreen) fires a separate pointerdown for each
                    // finger — without this guard, finger 2's pointerdown
                    // would arm a fresh timer after the native touchstart
                    // had cancelled finger 1's.
                    if (activeTouchCountRef.current > 1) { longPressFired.current = true; return; }
                    // Always clear any pending timer from a previous
                    // pointerdown so we can't leak a running timer when
                    // React fires back-to-back pointerdowns.
                    clearTimeout(longPressRef.current); longPressRef.current = null;
                    longPressFired.current = false;
                    longPressOrigin.current = { x: e.clientX, y: e.clientY };
                    longPressRef.current = setTimeout(() => {
                      // Abort if a two-finger gesture is active at fire time
                      // — catches races where the second finger arrives after
                      // the 500 ms timer starts.
                      if (activeTouchCountRef.current > 1) { longPressFired.current = true; return; }
                      longPressFired.current = true; haptic("longpress"); onRouteLongPress(r);
                    }, 500);
                  }}
                  onPointerMove={(e) => { if (!longPressRef.current || !longPressOrigin.current) return; const dx = e.clientX - longPressOrigin.current.x, dy = e.clientY - longPressOrigin.current.y; if (dx * dx + dy * dy > 100) { clearTimeout(longPressRef.current); longPressRef.current = null; } }}
                  onPointerUp={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                  onPointerCancel={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                  onPointerLeave={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                  style={{ cursor: isPlacing ? "crosshair" : "pointer" }}>
                  {isNew && (
                    <circle cx={rx} cy={ry} r="8" fill="none" stroke={pulseColor} strokeWidth="1.5">
                      <animate attributeName="r" values="8;12;8" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.4;0.15;0.4" dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                  {isTapped && (
                    <circle
                      key={`pulse-mini-${r.id}-${tappedId}`}
                      className="sendly-tap-ring"
                      cx={rx} cy={ry} r="22"
                      fill="none" stroke={accent} strokeWidth="3"
                    />
                  )}
                  {isCompRoute && (
                    <circle cx={rx} cy={ry} r="7.5" fill="none" stroke="#f59e0b" strokeWidth="1" strokeDasharray="2,1.5" opacity="0.9" />
                  )}
                  <circle cx={rx} cy={ry} r="5" fill={smallFill} stroke={hasH ? "none" : accent} strokeWidth={hasH ? "0" : "1"} opacity="0.92" />
                  {ok && <circle cx={rx} cy={ry} r="2" fill={accent} />}
                </g>
              );
            }

            const matchesSearch = isSearchMatch(r);
            const isNew = isNewRoute(r);
            const isTapped = tappedId === r.id;
            return (
              <g key={r.id} data-testid="map-marker" data-route-id={r.id}
                onClick={(e) => { if (isPlacing || longPressFired.current) return; e.stopPropagation(); flashTap(r.id); setTimeout(() => onRouteClick(r), 180); }}
                onPointerDown={(e) => {
                  if (isPlacing || !onRouteLongPress) return;
                  // Don't arm a new timer if a multi-touch gesture is already
                  // in progress (see matching comment on the compact marker).
                  if (activeTouchCountRef.current > 1) { longPressFired.current = true; return; }
                  clearTimeout(longPressRef.current); longPressRef.current = null;
                  longPressFired.current = false;
                  longPressOrigin.current = { x: e.clientX, y: e.clientY };
                  longPressRef.current = setTimeout(() => {
                    if (activeTouchCountRef.current > 1) { longPressFired.current = true; return; }
                    longPressFired.current = true; haptic("longpress"); onRouteLongPress(r);
                  }, 500);
                }}
                onPointerMove={(e) => { if (!longPressRef.current || !longPressOrigin.current) return; const dx = e.clientX - longPressOrigin.current.x, dy = e.clientY - longPressOrigin.current.y; if (dx * dx + dy * dy > 100) { clearTimeout(longPressRef.current); longPressRef.current = null; } }}
                onPointerUp={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                onPointerCancel={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                onPointerLeave={() => { clearTimeout(longPressRef.current); longPressRef.current = null; }}
                style={{ cursor: isPlacing ? "crosshair" : "pointer" }}
                transform={`translate(${rx}, ${ry}) scale(${dotScale})`}>
                {matchesSearch && (
                  <circle cx={0} cy={0} r="20" fill="none" stroke={accent} strokeWidth="2.5">
                    <animate attributeName="r" values="20;24;20" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                )}
                {isNew && !matchesSearch && (
                  <circle cx={0} cy={0} r="16" fill="none" stroke={pulseColor} strokeWidth="2">
                    <animate attributeName="r" values="16;22;16" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.3;0.15;0.3" dur="2s" repeatCount="indefinite" />
                  </circle>
                )}
                {isTapped && (
                  <circle
                    key={`pulse-big-${r.id}-${tappedId}`}
                    className="sendly-tap-ring"
                    cx={0} cy={0} r="36"
                    fill="none" stroke={accent} strokeWidth="3"
                  />
                )}
                {isCompRoute && (
                  <circle cx={0} cy={0} r="17.5" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="3,2" opacity="0.9" />
                )}
                <circle cx={0} cy={0} r="14" fill={markerFill} stroke={hasH ? "none" : accent} strokeWidth={hasH ? "0" : "1.5"} opacity="0.9" />
                {hasD && (
                  <>
                    <circle cx={0} cy={0} r="7" fill={innerColor} stroke={innerBorder} strokeWidth="2" />
                    {pointsText && (
                      <text x={0} y={0.5} textAnchor="middle" dominantBaseline="middle"
                        fill={innerTc} fontSize="7" fontWeight="700" fontFamily="'Space Grotesk',sans-serif"
                      >{pointsText}</text>
                    )}
                  </>
                )}
                {!hasD && (
                  <text x={0} y={0.5} textAnchor="middle" dominantBaseline="middle"
                    fill={hasH ? "#fff" : accent} fontSize="9" fontWeight="700" fontFamily="'Space Grotesk',sans-serif"
                  >?</text>
                )}
                {ok && (
                  <g transform="translate(12, -12)">
                    <circle r="6" fill={accent} stroke="#fff" strokeWidth="1.5" />
                    <polyline points="-2.5,0.5 -0.5,2.5 3,-1.5" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                )}
                {/* Quick-log tally goes bottom-right: top-right is the climbed
                    check, top-left the competition badge. */}
                {quickLogActive && (quickLogCounts?.[r.id] > 0) && (
                  <g transform="translate(12, 12)">
                    <rect x={-9} y={-6.5} width={18} height={13} rx={6.5} fill={accent} stroke="#fff" strokeWidth="1.5" />
                    <text x={0} y={0.5} textAnchor="middle" dominantBaseline="middle" fontSize="8" fontWeight="800" fill="#fff">
                      ×{quickLogCounts[r.id]}
                    </text>
                  </g>
                )}
                {isCompRoute && (
                  <g transform="translate(-12, -12)">
                    <circle r="6.5" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
                    <text x={0} y={0.5} textAnchor="middle" dominantBaseline="middle" fontSize="7">🏆</text>
                  </g>
                )}
              </g>
            );
          });
        })()}

        {/* Spray wall markers (Spraywall add-on, §10 / D49 / D90 item 11).
            `sprayWalls` arrives already filtered to the live walls of THIS
            map (visibleMapWalls in MapTab), so the only skip here is the
            wall being moved in placement mode — like repositionId hides a
            route. Drawn after the routes so they sit on top. A square glyph
            and NO text label; the name is only the aria-label. Not tied to
            the route filters, the search or the route-count badge.
            Inert in quick-log, archive and placement mode — and inert means
            NOT stopping propagation, so in placement mode the tap still
            reaches handleSvgClick and places. */}
        {sprayWalls.map(w => {
          if (placingSprayWallId && w.id === placingSprayWallId) return null;
          const p = rotation === 0 ? { x: w.x, y: w.y } : rotatePoint(w.x, w.y, rotation, mapWidth, mapHeight);
          // Both branches are a fixed SCREEN size, so the marker does not
          // depend on the map image's resolution: ~51px zoomed in, 32px out.
          const scale = showDetail ? dotScale : sprayMarkerFarScale(viewBox.w, cw);
          const tapKey = "sw:" + w.id;
          return (
            <g key={tapKey}
              role="button"
              aria-label={w.name || undefined}
              transform={`translate(${p.x}, ${p.y}) scale(${scale})`}
              style={{ cursor: isPlacing ? "crosshair" : "pointer" }}
              onClick={(e) => {
                if (isPlacing || quickLogActive || archiveActive || !onSprayWallClick) return;
                e.stopPropagation();
                flashTap(tapKey);
                setTimeout(() => onSprayWallClick(w.id), 180);
              }}>
              {tappedId === tapKey && (
                <circle
                  key={`pulse-sw-${w.id}-${tappedId}`}
                  className="sendly-tap-ring"
                  cx={0} cy={0} r="36"
                  fill="none" stroke={accent} strokeWidth="3"
                />
              )}
              <SprayWallMarkerGlyph />
            </g>
          );
        })}
      </svg>

      {/* Zoom controls + fullscreen \u2014 rotation is gesture-only (pinch + twist)
          on touch devices, no separate button. On desktop the orientation
          stays at whatever the touch user last left it (persisted per map),
          which is fine since desktop users rarely face the wall from a
          different direction the way someone with a phone in the gym does. */}
      {/* This stack is the ONLY entry point for quick log and the filter sheet.
          The chip row above the map would have been the obvious home for a
          toggle, but it only renders when a filter is active \u2014 and neither it
          nor any other chrome exists in fullscreen, which is exactly where
          someone standing at the wall with a zoomed-in map is. One always-
          visible place beats two conditional ones. */}
      <div style={{ position: "absolute", bottom: ctrlBottom, right: 12, display: "flex", flexDirection: "column", gap: 4, zIndex: 15 }}>
        {[
          // No +/− buttons: wheel and pinch already zoom on every
          // platform the app runs on, and the two slots they took are worth
          // more as room for the mode toggles below — which have to fit in the
          // SMALL map too, not just fullscreen.
          { key: "reset", label: "\u27F2", fn: resetView, title: t('zoomMap.reset') },
          // Same sliders glyph as the header's filter button (App.js) \u2014 a gear
          // would read as "settings", which is a different thing entirely.
          onOpenFilters && {
            key: "filter",
            label: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
              </svg>
            ),
            fn: onOpenFilters,
            title: t('common.toggleFilters'),
            // Mirrors the header button's active state, so both filter entry
            // points look the same when a filter is on.
            on: activeFilterCount > 0,
          },
          onToggleQuickLog && { key: "quick", label: "\u26A1", fn: onToggleQuickLog, title: t('quickLog.toggle'), on: quickLogActive, toggle: true },
          // Archive mode. Available in the small map too — a setter stripping a
          // wall should not have to go fullscreen first. Safety comes from the
          // banner, the per-tap undo and the disarm rules, not from hiding the
          // button.
          onToggleArchive && {
            key: "archive",
            label: (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="5" rx="1" />
                <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
            ),
            fn: onToggleArchive,
            title: t('archiveMode.toggle'),
            on: archiveActive,
            toggle: true,
          },
          { key: "fs", label: isFullscreen ? "\u2716" : "\u26F6", fn: toggleFullscreen, title: isFullscreen ? t('zoomMap.exitFullscreen') : t('zoomMap.fullscreen') },
        ].filter(Boolean).map(b => (
          // The two modes are toggle buttons: they always say whether they are on.
          // The filter button opens a panel; it is marked pressed only while a
          // filter is live, mirroring the header's filter button.
          <button key={b.key} type="button" onClick={b.fn} title={b.title} aria-label={b.title} aria-pressed={b.toggle ? !!b.on : (b.on ? true : undefined)} style={{
            width: 36, height: 36, borderRadius: 10,
            border: "1px solid " + (b.on ? accent : "#ddd"),
            background: b.on ? accent : "#fff", color: b.on ? "#fff" : "#333",
            fontSize: 18, cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontWeight: 700, backdropFilter: "blur(8px)",
          }}>{b.label}</button>
        ))}
      </div>
    </div>
  );
}
